import axios, { AxiosInstance } from 'axios';
import { AuthApi } from './auth.api';
import { CampaignApi } from './campaign.api';
import { CustomerApi } from './customer.api';
import { ExpertisePointApi } from './expertise-point.api';
import { ExpertiseApi } from './expertise.api';
import { IntegrationApi } from './integration.api';
import { LocationApi } from './location.api';
import { OcrApi } from './ocr.api';
import { ReportingApi } from './reporting.api';
import { UserApi } from './user.api';
import { UtilApi } from './util.api';
import { VehicleApi } from './vehicle.api';
import { OrderApi } from './order.api';
import { ProductApi } from './product.api';
import { QrApi } from './qr.api';

interface ApiFuncs {
  Auth: AuthApi;
  Customer: CustomerApi;
  Campaign: CampaignApi;
  Vehicle: VehicleApi;
  Location: LocationApi;
  Expertise: ExpertiseApi;
  Integration: IntegrationApi;
  Ocr: OcrApi;
  ExpertisePoint: ExpertisePointApi;
  Util: UtilApi;
  Reporting: ReportingApi;
  User: UserApi;
  Order: OrderApi;
  Product: ProductApi;
  Qr: QrApi;
}

export class BackendApi {
  private _token?: string;

  private _language: string = 'tr';

  private axiosInstace: AxiosInstance;

  private apiFuncs: ApiFuncs;

  // eslint-disable-next-line no-use-before-define
  private static _instance: BackendApi;

  private constructor() {
    this.axiosInstace = axios.create({
      baseURL: process.env.REACT_APP_BASE_URL,
      timeout: 60000,
      headers: {
        'x-custom-lang': this._language,
      },
    });
    this.apiFuncs = this.generateApiFuncs();
  }

  public get token(): string | undefined {
    return this._token;
  }

  public set token(newToken: string | undefined) {
    this._token = newToken;
    if (newToken) {
      this.axiosInstace = axios.create({
        baseURL: process.env.REACT_APP_BASE_URL,
        timeout: 60000,
        headers: {
          Authorization: `Bearer ${newToken}`,
          'x-custom-lang': this._language,
        },
      });
      this.apiFuncs = this.generateApiFuncs();
      this.setupInterceptors();
    }
  }

  public get language(): string {
    return this._language;
  }

  public set language(newLanguage: string) {
    this._language = newLanguage;
    this.axiosInstace = axios.create({
      baseURL: process.env.REACT_APP_BASE_URL,
      timeout: 60000,
      headers: {
        'x-custom-lang': newLanguage,
        ...(this._token ? { Authorization: `Bearer ${this._token}` } : {}),
      },
    });
    this.apiFuncs = this.generateApiFuncs();
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.axiosInstace.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('authUserInfo');
          this._token = undefined;
          if (!window.location.pathname.includes('/auth/login')) {
            window.location.href = '/auth/login';
          }
        }
        return Promise.reject(error);
      },
    );
  }

  private generateApiFuncs(): ApiFuncs {
    return {
      Auth: new AuthApi(this.axiosInstace),
      Campaign: new CampaignApi(this.axiosInstace),
      Customer: new CustomerApi(this.axiosInstace),
      Vehicle: new VehicleApi(this.axiosInstace),
      Location: new LocationApi(this.axiosInstace),
      Expertise: new ExpertiseApi(this.axiosInstace),
      Integration: new IntegrationApi(this.axiosInstace),
      Ocr: new OcrApi(this.axiosInstace),
      ExpertisePoint: new ExpertisePointApi(this.axiosInstace),
      Util: new UtilApi(this.axiosInstace),
      Reporting: new ReportingApi(this.axiosInstace),
      User: new UserApi(this.axiosInstace),
      Order: new OrderApi(this.axiosInstace),
      Product: new ProductApi(this.axiosInstace),
      Qr: new QrApi(this.axiosInstace),
    };
  }

  public static get Instance(): BackendApi {
    if (!this._instance) {
      this._instance = new BackendApi();
    }
    return this._instance;
  }

  // #region Api Etkilesim fonksiyonlari
  public get Auth(): AuthApi {
    return this.apiFuncs.Auth;
  }

  public get Customer(): CustomerApi {
    return this.apiFuncs.Customer;
  }

  public get Campaign(): CampaignApi {
    return this.apiFuncs.Campaign;
  }

  public get Vehicle(): VehicleApi {
    return this.apiFuncs.Vehicle;
  }

  public get Location(): LocationApi {
    return this.apiFuncs.Location;
  }

  public get Expertise(): ExpertiseApi {
    return this.apiFuncs.Expertise;
  }

  public get Integration(): IntegrationApi {
    return this.apiFuncs.Integration;
  }

  public get Ocr(): OcrApi {
    return this.apiFuncs.Ocr;
  }

  public get ExpertisePoint(): ExpertisePointApi {
    return this.apiFuncs.ExpertisePoint;
  }

  public get Util(): UtilApi {
    return this.apiFuncs.Util;
  }

  public get Reporting(): ReportingApi {
    return this.apiFuncs.Reporting;
  }

  public get User(): UserApi {
    return this.apiFuncs.User;
  }

  public get Order(): OrderApi {
    return this.apiFuncs.Order;
  }

  public get Product(): ProductApi {
    return this.apiFuncs.Product;
  }

  public get Qr(): QrApi {
    return this.apiFuncs.Qr;
  }
  // #endregion
}

export const Api = BackendApi.Instance;
